// src/modules/aiml/MediaPipeSection.jsx
// MediaPipe Tasks Vision — Face · Hands · Pose · Gesture · Object · Segment
// Tabs: overview | face | hands | pose | gesture | object | segment | concepts

import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { ModelLoader, InferenceTag } from './AIMLModule.jsx';

const ACCENT = '#06b6d4';
import { _livePalette as P } from '../../context/paletteSync';
const TABS = ['overview','face','hands','pose','gesture','object','segment','concepts'];

const Btn = ({ onClick, children, color = ACCENT, disabled, style }) => (
  <button onClick={onClick} disabled={disabled} style={{ padding: '8px 18px', background: color + '18', border: `1px solid ${color}`, color, fontSize: '10px', cursor: disabled ? 'not-allowed' : 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono', fontWeight: 700, opacity: disabled ? 0.4 : 1, ...style }}>{children}</button>
);
const Tag = ({ children, color = ACCENT }) => (
  <span style={{ padding: '2px 8px', background: color + '20', border: `1px solid ${color}44`, color, fontSize: '9px', borderRadius: '2px', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>{children}</span>
);
const Panel = ({ title, accent = ACCENT, children, style }) => (
  <div style={{ background: 'var(--surface)', border: '1px solid var(--b0)', borderRadius: '4px', overflow: 'hidden', ...style }}>
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--b0)', fontSize: '9px', letterSpacing: '.1em', color: accent, fontWeight: 700, fontFamily: 'JetBrains Mono' }}>{title}</div>
    <div style={{ padding: '14px' }}>{children}</div>
  </div>
);

// ── MediaPipe CDN ─────────────────────────────────────────────────
const MP_CDN = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/vision_bundle.js';

let _mpLoaded = false;
let _mpVision = null;

async function ensureMP() {
  if (_mpLoaded && _mpVision) return _mpVision;
  await new Promise((res, rej) => {
    if (window.mpVision) { _mpLoaded = true; res(); return; }
    const s = document.createElement('script');
    s.src = MP_CDN; s.type = 'module';
    // For module scripts we need a different approach — use dynamic import
    document.head.appendChild(s);
    s.onload = res; s.onerror = rej;
  });
  return null;
}

// Use dynamic import for MediaPipe (ESM)
const MP_ESM = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/vision_bundle.js';

async function loadMPVision() {
  if (_mpVision) return _mpVision;
  try {
    _mpVision = await import(/* webpackIgnore: true */ MP_ESM);
    _mpLoaded = true;
    return _mpVision;
  } catch (e) {
    // Fallback: load via script tag and use global
    console.warn('ESM import failed, trying UMD fallback', e);
    return null;
  }
}

// ── Canvas drawing helpers ────────────────────────────────────────
const FACE_OVAL = [10,338,297,332,284,251,389,356,454,323,361,288,397,365,379,378,400,377,152,148,176,149,150,136,172,58,132,93,234,127,162,21,54,103,67,109];
const FACE_LIPS_OUTER = [61,185,40,39,37,0,267,269,270,409,291,375,321,405,314,17,84,181,91,146];
const FACE_LEFT_EYE  = [33,7,163,144,145,153,154,155,133,173,157,158,159,160,161,246];
const FACE_RIGHT_EYE = [362,382,381,380,374,373,390,249,263,466,388,387,386,385,384,398];

function drawFaceLandmarks(canvas, landmarks, imgEl, mode = 'dots') {
  if (!canvas || !landmarks) return;
  const ctx = canvas.getContext('2d');
  canvas.width  = imgEl.naturalWidth  || imgEl.width;
  canvas.height = imgEl.naturalHeight || imgEl.height;
  ctx.drawImage(imgEl, 0, 0);
  const W = canvas.width, H = canvas.height;

  if (mode === 'mesh') {
    ctx.strokeStyle = 'rgba(6,182,212,0.4)';
    ctx.lineWidth = 0.5;
    // Draw connections (sample subset)
    for (let i = 0; i < FACE_OVAL.length - 1; i++) {
      const a = landmarks[FACE_OVAL[i]], b = landmarks[FACE_OVAL[i+1]];
      if (!a || !b) continue;
      ctx.beginPath(); ctx.moveTo(a.x * W, a.y * H); ctx.lineTo(b.x * W, b.y * H); ctx.stroke();
    }
    [FACE_LIPS_OUTER, FACE_LEFT_EYE, FACE_RIGHT_EYE].forEach(group => {
      group.forEach((idx, i) => {
        if (i === 0) return;
        const a = landmarks[group[i-1]], b = landmarks[idx];
        if (!a || !b) return;
        ctx.beginPath(); ctx.moveTo(a.x * W, a.y * H); ctx.lineTo(b.x * W, b.y * H); ctx.stroke();
      });
    });
  }

  // Draw all keypoints
  landmarks.forEach((pt, i) => {
    ctx.beginPath();
    ctx.arc(pt.x * W, pt.y * H, mode === 'dots' ? 1.5 : 1, 0, Math.PI * 2);
    ctx.fillStyle = FACE_OVAL.includes(i) ? '#f59e0b' : FACE_LEFT_EYE.includes(i) || FACE_RIGHT_EYE.includes(i) ? '#f472b6' : 'rgba(6,182,212,0.6)';
    ctx.fill();
  });
}

function drawHandLandmarks(canvas, hands, imgEl) {
  if (!canvas || !hands?.length) return;
  const ctx = canvas.getContext('2d');
  canvas.width  = imgEl.naturalWidth  || imgEl.width;
  canvas.height = imgEl.naturalHeight || imgEl.height;
  ctx.drawImage(imgEl, 0, 0);
  const W = canvas.width, H = canvas.height;

  const CONNECTIONS = [[0,1],[1,2],[2,3],[3,4],[0,5],[5,6],[6,7],[7,8],[0,9],[9,10],[10,11],[11,12],[0,13],[13,14],[14,15],[15,16],[0,17],[17,18],[18,19],[19,20],[5,9],[9,13],[13,17]];
  const COLORS = ['#34d399','#f59e0b'];

  hands.forEach(({ landmarks, handedness }, hi) => {
    const col = COLORS[hi % 2];
    CONNECTIONS.forEach(([a, b]) => {
      const pa = landmarks[a], pb = landmarks[b];
      ctx.beginPath(); ctx.moveTo(pa.x * W, pa.y * H); ctx.lineTo(pb.x * W, pb.y * H);
      ctx.strokeStyle = col; ctx.lineWidth = 2; ctx.stroke();
    });
    landmarks.forEach((pt, i) => {
      ctx.beginPath(); ctx.arc(pt.x * W, pt.y * H, i === 0 ? 7 : 4, 0, Math.PI * 2);
      ctx.fillStyle = i === 0 ? '#fff' : col; ctx.fill();
    });
    // Label
    if (handedness?.[0]) {
      ctx.font = 'bold 14px sans-serif'; ctx.fillStyle = col;
      ctx.fillText(handedness[0].displayName, landmarks[0].x * W, landmarks[0].y * H - 12);
    }
  });
}

function drawPoseLandmarks(canvas, landmarks, imgEl) {
  if (!canvas || !landmarks) return;
  const ctx = canvas.getContext('2d');
  canvas.width  = imgEl.naturalWidth  || imgEl.width;
  canvas.height = imgEl.naturalHeight || imgEl.height;
  ctx.drawImage(imgEl, 0, 0);
  const W = canvas.width, H = canvas.height;

  const CONNECTIONS = [[0,1],[1,2],[2,3],[3,7],[0,4],[4,5],[5,6],[6,8],[9,10],[11,12],[11,13],[13,15],[15,17],[15,19],[17,19],[12,14],[14,16],[16,18],[16,20],[18,20],[11,23],[12,24],[23,24],[23,25],[24,26],[25,27],[26,28],[27,29],[28,30],[29,31],[30,32],[27,31],[28,32]];

  CONNECTIONS.forEach(([a, b]) => {
    const pa = landmarks[a], pb = landmarks[b];
    if (!pa || !pb || pa.visibility < 0.3 || pb.visibility < 0.3) return;
    ctx.beginPath(); ctx.moveTo(pa.x * W, pa.y * H); ctx.lineTo(pb.x * W, pb.y * H);
    ctx.strokeStyle = '#34d399'; ctx.lineWidth = 2.5; ctx.stroke();
  });
  landmarks.forEach(pt => {
    if (pt.visibility < 0.3) return;
    ctx.beginPath(); ctx.arc(pt.x * W, pt.y * H, 5, 0, Math.PI * 2);
    ctx.fillStyle = '#f59e0b'; ctx.fill();
    ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.5; ctx.stroke();
  });
}

// ── Image upload helper ────────────────────────────────────────────
function ImageUploader({ id, src, onSrc, color = ACCENT, label = '📁 UPLOAD IMAGE' }) {
  return (
    <label style={{ display: 'inline-block', padding: '8px 16px', background: color + '18', border: `1px solid ${color}`, color, fontSize: '10px', cursor: 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>
      {label}
      <input type="file" accept="image/*" onChange={e => { const f = e.target.files[0]; if (f) onSrc(URL.createObjectURL(f)); }} style={{ display: 'none' }} />
      {src && <img id={id} src={src} crossOrigin="anonymous" style={{ display: 'none' }} alt="" />}
    </label>
  );
}

// ═════════════════════════════════════════════════════════════════
export default function MediaPipeSection({ gpuInfo }) {
  const [tab, setTab] = useState('overview');

  // Face
  const [faceStatus, setFaceStatus]   = useState('idle');
  const [faceProgress, setFaceProgress] = useState(0);
  const [faceImgSrc, setFaceImgSrc]   = useState(null);
  const [faceResult, setFaceResult]   = useState(null);
  const [faceTime, setFaceTime]       = useState(null);
  const [faceMode, setFaceMode]       = useState('dots'); // dots | mesh | contours
  const faceCanvas = useRef(null);
  const faceRef    = useRef(null);

  // Hands
  const [handsStatus, setHandsStatus]   = useState('idle');
  const [handsProgress, setHandsProgress] = useState(0);
  const [handsImgSrc, setHandsImgSrc]   = useState(null);
  const [handsResult, setHandsResult]   = useState(null);
  const [handsTime, setHandsTime]       = useState(null);
  const handsCanvas = useRef(null);
  const handsRef    = useRef(null);

  // Pose
  const [poseStatus, setPoseStatus]   = useState('idle');
  const [poseProgress, setPoseProgress] = useState(0);
  const [poseImgSrc, setPoseImgSrc]   = useState(null);
  const [poseResult, setPoseResult]   = useState(null);
  const [poseTime, setPoseTime]       = useState(null);
  const poseCanvas = useRef(null);
  const poseRef    = useRef(null);

  // Object
  const [objStatus, setObjStatus]   = useState('idle');
  const [objProgress, setObjProgress] = useState(0);
  const [objImgSrc, setObjImgSrc]   = useState(null);
  const [objResult, setObjResult]   = useState(null);
  const [objTime, setObjTime]       = useState(null);
  const objCanvas = useRef(null);
  const objRef    = useRef(null);

  // Gesture demo (simulated — real gesture recognition needs webcam)
  const [gestureLabel, setGestureLabel] = useState('');
  const [gestureConf, setGestureConf]   = useState(0);
  const [gestureHistory, setGestureHistory] = useState([]);

  // ── LOAD HELPERS ──────────────────────────────────────────────
  const loadFace = async () => {
    setFaceStatus('downloading'); setFaceProgress(20);
    try {
      const mp = await loadMPVision();
      if (!mp) throw new Error('MediaPipe import failed');
      setFaceProgress(60); setFaceStatus('loading');
      const { FaceLandmarker, FilesetResolver } = mp;
      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm'
      );
      faceRef.current = await FaceLandmarker.createFromOptions(vision, {
        baseOptions: { modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task', delegate: 'GPU' },
        runningMode: 'IMAGE', numFaces: 1,
      });
      setFaceProgress(100); setFaceStatus('ready');
    } catch (e) { console.error('Face load error:', e); setFaceStatus('error'); }
  };

  const runFace = async () => {
    if (!faceRef.current || !faceImgSrc) return;
    const img = document.getElementById('face-img');
    if (!img || !img.complete) return;
    const t0 = performance.now();
    try {
      const result = faceRef.current.detect(img);
      setFaceTime(Math.round(performance.now() - t0));
      setFaceResult(result);
      if (result.faceLandmarks?.[0]) {
        drawFaceLandmarks(faceCanvas.current, result.faceLandmarks[0], img, faceMode);
      }
    } catch (e) { console.error('Face run error:', e); }
  };

  useEffect(() => {
    if (faceResult?.faceLandmarks?.[0] && faceImgSrc) {
      const img = document.getElementById('face-img');
      if (img?.complete) drawFaceLandmarks(faceCanvas.current, faceResult.faceLandmarks[0], img, faceMode);
    }
  }, [faceMode]);

  const loadHands = async () => {
    setHandsStatus('downloading'); setHandsProgress(20);
    try {
      const mp = await loadMPVision();
      if (!mp) throw new Error('MediaPipe import failed');
      setHandsProgress(60); setHandsStatus('loading');
      const { HandLandmarker, FilesetResolver } = mp;
      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm'
      );
      handsRef.current = await HandLandmarker.createFromOptions(vision, {
        baseOptions: { modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task', delegate: 'GPU' },
        runningMode: 'IMAGE', numHands: 2,
      });
      setHandsProgress(100); setHandsStatus('ready');
    } catch (e) { console.error('Hands load error:', e); setHandsStatus('error'); }
  };

  const runHands = async () => {
    if (!handsRef.current || !handsImgSrc) return;
    const img = document.getElementById('hands-img');
    if (!img?.complete) return;
    const t0 = performance.now();
    try {
      const result = handsRef.current.detect(img);
      setHandsTime(Math.round(performance.now() - t0));
      setHandsResult(result);
      if (result.landmarks?.length) {
        drawHandLandmarks(handsCanvas.current, result.landmarks.map((lm, i) => ({
          landmarks: lm, handedness: result.handedness?.[i],
        })), img);
      }
    } catch (e) { console.error('Hands run error:', e); }
  };

  const loadPose = async () => {
    setPoseStatus('downloading'); setPoseProgress(20);
    try {
      const mp = await loadMPVision();
      if (!mp) throw new Error('MediaPipe import failed');
      setPoseProgress(60); setPoseStatus('loading');
      const { PoseLandmarker, FilesetResolver } = mp;
      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm'
      );
      poseRef.current = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: { modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task', delegate: 'GPU' },
        runningMode: 'IMAGE', numPoses: 1,
      });
      setPoseProgress(100); setPoseStatus('ready');
    } catch (e) { console.error('Pose load error:', e); setPoseStatus('error'); }
  };

  const runPose = async () => {
    if (!poseRef.current || !poseImgSrc) return;
    const img = document.getElementById('pose-img');
    if (!img?.complete) return;
    const t0 = performance.now();
    try {
      const result = poseRef.current.detect(img);
      setPoseTime(Math.round(performance.now() - t0));
      setPoseResult(result);
      if (result.landmarks?.[0]) drawPoseLandmarks(poseCanvas.current, result.landmarks[0], img);
    } catch (e) { console.error('Pose run error:', e); }
  };

  const loadObj = async () => {
    setObjStatus('downloading'); setObjProgress(20);
    try {
      const mp = await loadMPVision();
      if (!mp) throw new Error('MediaPipe import failed');
      setObjProgress(60); setObjStatus('loading');
      const { ObjectDetector, FilesetResolver } = mp;
      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm'
      );
      objRef.current = await ObjectDetector.createFromOptions(vision, {
        baseOptions: { modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/object_detector/efficientdet_lite0/float16/1/efficientdet_lite0.tflite', delegate: 'GPU' },
        runningMode: 'IMAGE', maxResults: 10, scoreThreshold: 0.4,
      });
      setObjProgress(100); setObjStatus('ready');
    } catch (e) { console.error('Obj load error:', e); setObjStatus('error'); }
  };

  const runObj = async () => {
    if (!objRef.current || !objImgSrc) return;
    const img = document.getElementById('obj-img');
    if (!img?.complete) return;
    const t0 = performance.now();
    try {
      const result = objRef.current.detect(img);
      setObjTime(Math.round(performance.now() - t0));
      setObjResult(result);
      // Draw boxes
      const canvas = objCanvas.current;
      if (!canvas) return;
      canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      result.detections?.forEach(({ categories, boundingBox }, i) => {
        const { originX: x, originY: y, width: w, height: h } = boundingBox;
        const col = P[i % P.length];
        const label = `${categories[0]?.categoryName} ${(categories[0]?.score * 100).toFixed(0)}%`;
        ctx.strokeStyle = col; ctx.lineWidth = 3; ctx.strokeRect(x, y, w, h);
        ctx.fillStyle = col; ctx.fillRect(x, y - 22, label.length * 7.5, 22);
        ctx.fillStyle = '#000'; ctx.font = 'bold 12px monospace';
        ctx.fillText(label, x + 4, y - 5);
      });
    } catch (e) { console.error('Obj run error:', e); }
  };

  // Simulated gesture recognizer for demo
  const GESTURES = ['👍 Thumbs Up','👋 Open Hand','✌️ Peace','👊 Fist','☝️ Pointing Up','👎 Thumbs Down','🤘 Rock On','🤙 Call Me'];
  const simulateGesture = () => {
    const g = GESTURES[Math.floor(Math.random() * GESTURES.length)];
    const conf = 0.7 + Math.random() * 0.3;
    setGestureLabel(g); setGestureConf(conf);
    setGestureHistory(h => [...h.slice(-8), { gesture: g, score: +conf.toFixed(2) }]);
  };

  return (
    <div className="anim-fadeup" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', gap: '3px', background: 'var(--deep)', padding: '4px', borderRadius: '3px', border: '1px solid var(--b0)', overflowX: 'auto' }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '6px 14px', border: `1px solid ${tab === t ? ACCENT : 'transparent'}`, background: tab === t ? ACCENT + '18' : 'transparent', color: tab === t ? ACCENT : 'var(--t3)', fontSize: '10px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono', whiteSpace: 'nowrap', fontWeight: tab === t ? 700 : 400 }}>{t.toUpperCase()}</button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="MEDIAPIPE TASKS VISION — OVERVIEW" accent={ACCENT}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.85 }}>
                <strong style={{ color: ACCENT }}>MediaPipe Tasks Vision</strong> (Google 2023) provides production-quality vision models running in-browser via WASM + SIMD. Based on Google's production ML pipeline used in Google Meet, Pixel camera, and Android.<br /><br />
                The new Tasks API unifies all models under a single interface: <code style={{ color: ACCENT, fontFamily: 'JetBrains Mono' }}>Landmarker.createFromOptions()</code> → <code style={{ color: ACCENT, fontFamily: 'JetBrains Mono' }}>.detect(imageEl)</code>. Models are downloaded from Google Cloud Storage and cached.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                {[
                  ['Face Landmarker','478 3D keypoints per face',ACCENT],
                  ['Hand Landmarker','21 3D keypoints × 2 hands','var(--green)'],
                  ['Pose Landmarker','33 body keypoints + visibility','var(--violet)'],
                  ['Gesture Recognizer','9 built-in gestures','var(--gold)'],
                  ['Object Detector','EfficientDet, 80+ classes','var(--orange)'],
                  ['Image Segmenter','Selfie + general segmentation','var(--red)'],
                ].map(([k,v,c]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: 'var(--raised)', borderRadius: '3px' }}>
                    <span style={{ fontSize: '11px', color: 'var(--t3)' }}>{k}</span>
                    <span style={{ fontSize: '11px', color: c, fontFamily: 'JetBrains Mono', fontWeight: 700 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </Panel>

          <Panel title="TASK API PATTERN" accent={ACCENT}>
            <pre style={{ margin: 0, padding: '14px', background: 'var(--deep)', borderRadius: '3px', fontSize: '10px', color: 'var(--t2)', fontFamily: 'JetBrains Mono', lineHeight: 1.7 }}>
{`import { FaceLandmarker, FilesetResolver } from
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/vision_bundle.js';

// 1. Load WASM runtime
const vision = await FilesetResolver.forVisionTasks(
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm'
);

// 2. Create task with model URL
const faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
  baseOptions: {
    modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/...',
    delegate: 'GPU',    // WebGL acceleration
  },
  runningMode: 'IMAGE', // or 'VIDEO' for live webcam
  numFaces: 2,
});

// 3. Run on any <img> or <video> element
const result = faceLandmarker.detect(imageElement);
// result.faceLandmarks[0] → array of 478 {x, y, z} points`}
            </pre>
          </Panel>
        </div>
      )}

      {/* ── FACE ── */}
      {tab === 'face' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="FACE LANDMARKER — 478 3D KEYPOINTS" accent={ACCENT}>
            <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.7, marginBottom: '14px' }}>
              <strong style={{ color: ACCENT }}>MediaPipe Face Landmarker</strong> detects 478 3D facial keypoints including jaw, eyebrows, eyes, irises, nose, and lips. Also outputs face blendshapes (52 expression coefficients) and facial transformation matrix.
            </div>
            {faceStatus !== 'ready' && (
              <ModelLoader status={faceStatus} progress={faceProgress} modelName="face_landmarker" modelSize="~29MB" accent={ACCENT} onLoad={loadFace} />
            )}
          </Panel>

          {faceStatus === 'ready' && (
            <Panel title="DETECT LANDMARKS" accent={ACCENT}>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                <ImageUploader id="face-img" src={faceImgSrc} onSrc={setFaceImgSrc} color={ACCENT} />
                {faceImgSrc && (
                  <>
                    <Btn onClick={runFace} color={ACCENT}>DETECT FACE →</Btn>
                    <span style={{ fontSize: '10px', color: 'var(--t3)' }}>mode:</span>
                    {['dots','mesh','contours'].map(m => (
                      <button key={m} onClick={() => setFaceMode(m)} style={{ padding: '5px 10px', border: `1px solid ${faceMode === m ? ACCENT : 'var(--b0)'}`, background: faceMode === m ? ACCENT + '18' : 'transparent', color: faceMode === m ? ACCENT : 'var(--t3)', fontSize: '9px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono' }}>{m}</button>
                    ))}
                    {faceTime && <InferenceTag ms={faceTime} />}
                  </>
                )}
              </div>
              {faceImgSrc && (
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '14px', alignItems: 'start' }}>
                  <div>
                    <img id="face-img" src={faceImgSrc} alt="" style={{ display: 'none' }} crossOrigin="anonymous" />
                    <canvas ref={faceCanvas} style={{ maxWidth: '300px', borderRadius: '4px', border: '1px solid var(--b0)', display: 'block' }} />
                  </div>
                  {faceResult && (
                    <div>
                      <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '8px', letterSpacing: '.1em' }}>
                        {faceResult.faceLandmarks?.length || 0} FACE(S) DETECTED · 478 KEYPOINTS EACH
                      </div>
                      {faceResult.faceBlendshapes?.[0]?.categories?.slice(0, 10).map(({ categoryName, score }) => (
                        <div key={categoryName} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontSize: '9px', color: 'var(--t3)', minWidth: '140px', fontFamily: 'JetBrains Mono' }}>{categoryName}</span>
                          <div style={{ flex: 1, height: '4px', background: 'var(--deep)', borderRadius: '2px' }}>
                            <div style={{ height: '100%', width: `${score * 100}%`, background: ACCENT, borderRadius: '2px' }} />
                          </div>
                          <span style={{ fontSize: '9px', color: ACCENT, fontFamily: 'JetBrains Mono', minWidth: '36px' }}>{(score * 100).toFixed(0)}%</span>
                        </div>
                      ))}
                      {faceResult.faceBlendshapes?.[0] && (
                        <div style={{ fontSize: '9px', color: 'var(--t3)', marginTop: '8px' }}>
                          (top 10 of 52 blendshape coefficients shown — expressions like browDown, eyeWide, mouthSmile…)
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Panel>
          )}
        </div>
      )}

      {/* ── HANDS ── */}
      {tab === 'hands' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="HAND LANDMARKER — 21 KEYPOINTS × 2 HANDS" accent="var(--green)">
            <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.7, marginBottom: '14px' }}>
              <strong style={{ color: 'var(--green)' }}>Hand Landmarker</strong> detects 21 3D landmarks per hand (wrist + 5 fingers × 4 joints each). Also outputs handedness confidence (Left/Right) for each detected hand.
            </div>
            {handsStatus !== 'ready' && (
              <ModelLoader status={handsStatus} progress={handsProgress} modelName="hand_landmarker" modelSize="~9MB" accent="var(--green)" onLoad={loadHands} />
            )}
          </Panel>

          {handsStatus === 'ready' && (
            <Panel title="DETECT HANDS" accent="var(--green)">
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                <ImageUploader id="hands-img" src={handsImgSrc} onSrc={setHandsImgSrc} color="var(--green)" />
                {handsImgSrc && <Btn onClick={runHands} color="var(--green)">DETECT HANDS →</Btn>}
                {handsTime && <InferenceTag ms={handsTime} />}
              </div>
              {handsImgSrc && (
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '14px', alignItems: 'start' }}>
                  <div>
                    <img id="hands-img" src={handsImgSrc} alt="" style={{ display: 'none' }} crossOrigin="anonymous" />
                    <canvas ref={handsCanvas} style={{ maxWidth: '300px', borderRadius: '4px', border: '1px solid var(--b0)', display: 'block' }} />
                  </div>
                  {handsResult && (
                    <div>
                      <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '10px', letterSpacing: '.1em' }}>
                        {handsResult.landmarks?.length || 0} HAND(S) · 21 LANDMARKS EACH
                      </div>
                      {handsResult.landmarks?.map((lm, hi) => (
                        <div key={hi} style={{ marginBottom: '12px' }}>
                          <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--green)', marginBottom: '6px' }}>
                            Hand {hi + 1}: {handsResult.handedness?.[hi]?.[0]?.displayName || '—'}
                            <span style={{ fontSize: '9px', color: 'var(--t3)', marginLeft: '8px' }}>conf: {((handsResult.handedness?.[hi]?.[0]?.score || 0) * 100).toFixed(0)}%</span>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '4px' }}>
                            {['Thumb','Index','Middle','Ring','Pinky'].map((finger, fi) => {
                              const joints = [fi * 4 + 1, fi * 4 + 2, fi * 4 + 3, fi * 4 + 4];
                              return (
                                <div key={finger} style={{ padding: '6px 8px', background: 'var(--raised)', borderRadius: '3px', textAlign: 'center' }}>
                                  <div style={{ fontSize: '9px', color: 'var(--green)', fontWeight: 700, marginBottom: '4px' }}>{finger}</div>
                                  {joints.map(j => lm[j] && (
                                    <div key={j} style={{ fontSize: '8px', color: 'var(--t3)', fontFamily: 'JetBrains Mono', lineHeight: 1.5 }}>
                                      {lm[j].x.toFixed(2)},{lm[j].y.toFixed(2)}
                                    </div>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Panel>
          )}
        </div>
      )}

      {/* ── POSE ── */}
      {tab === 'pose' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="POSE LANDMARKER — 33 BODY KEYPOINTS" accent="var(--violet)">
            <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.7, marginBottom: '14px' }}>
              <strong style={{ color: 'var(--violet)' }}>Pose Landmarker Lite</strong> outputs 33 body landmarks with x, y, z coordinates + visibility scores. Trained on a dataset of thousands of images. The "lite" variant (~7MB) balances speed and accuracy for browser use.
            </div>
            {poseStatus !== 'ready' && (
              <ModelLoader status={poseStatus} progress={poseProgress} modelName="pose_landmarker_lite" modelSize="~7MB" accent="var(--violet)" onLoad={loadPose} />
            )}
          </Panel>

          {poseStatus === 'ready' && (
            <Panel title="ESTIMATE POSE" accent="var(--violet)">
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                <ImageUploader id="pose-img" src={poseImgSrc} onSrc={setPoseImgSrc} color="var(--violet)" />
                {poseImgSrc && <Btn onClick={runPose} color="var(--violet)">ESTIMATE POSE →</Btn>}
                {poseTime && <InferenceTag ms={poseTime} />}
              </div>
              {poseImgSrc && (
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '14px', alignItems: 'start' }}>
                  <div>
                    <img id="pose-img" src={poseImgSrc} alt="" style={{ display: 'none' }} crossOrigin="anonymous" />
                    <canvas ref={poseCanvas} style={{ maxWidth: '320px', borderRadius: '4px', border: '1px solid var(--b0)', display: 'block' }} />
                  </div>
                  {poseResult?.landmarks?.[0] && (
                    <div>
                      <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '8px', letterSpacing: '.1em' }}>
                        33 KEYPOINTS — {poseResult.landmarks[0].filter(k => k.visibility > 0.5).length} VISIBLE
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                        {['nose','left_eye','right_eye','left_ear','right_ear','left_shoulder','right_shoulder','left_elbow','right_elbow','left_wrist','right_wrist','left_hip','right_hip','left_knee','right_knee','left_ankle','right_ankle'].map((name, i) => {
                          const kp = poseResult.landmarks[0][i];
                          return kp ? (
                            <div key={name} style={{ display: 'flex', gap: '6px', padding: '4px 8px', background: 'var(--raised)', borderRadius: '2px', opacity: (kp.visibility || 0) > 0.4 ? 1 : 0.35 }}>
                              <span style={{ fontSize: '9px', color: (kp.visibility || 0) > 0.6 ? 'var(--green)' : (kp.visibility || 0) > 0.3 ? 'var(--gold)' : 'var(--t3)', flex: 1, fontFamily: 'JetBrains Mono' }}>{name}</span>
                              <span style={{ fontSize: '8px', color: 'var(--t3)', fontFamily: 'JetBrains Mono' }}>{((kp.visibility || 0) * 100).toFixed(0)}%</span>
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Panel>
          )}
        </div>
      )}

      {/* ── GESTURE ── */}
      {tab === 'gesture' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="GESTURE RECOGNIZER — BUILT-IN HAND GESTURES" accent="var(--gold)">
            <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.75, marginBottom: '14px' }}>
              <strong style={{ color: 'var(--gold)' }}>Gesture Recognizer</strong> combines Hand Landmarker with a gesture classification head. Recognizes 9 built-in gestures: None, Closed Fist, Open Palm, Pointing Up, Thumbs Down, Thumbs Up, Victory (Peace), Love (ILY), and more. Real-time on webcam. The demo below simulates gesture recognition — enable webcam for live use.
            </div>

            {/* Gesture grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '8px', marginBottom: '14px' }}>
              {['👍 Thumbs Up','👎 Thumbs Down','✌️ Victory','✋ Open Palm','👊 Closed Fist','☝️ Pointing Up','🤟 ILY','🫱 None'].map((g, i) => (
                <div key={g} style={{ padding: '10px 12px', background: gestureLabel === g ? 'var(--gold)18' : 'var(--raised)', border: `1px solid ${gestureLabel === g ? 'var(--gold)' : 'var(--b0)'}`, borderRadius: '4px', textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', marginBottom: '4px' }}>{g.split(' ')[0]}</div>
                  <div style={{ fontSize: '10px', color: gestureLabel === g ? 'var(--gold)' : 'var(--t3)', fontFamily: 'JetBrains Mono', fontWeight: gestureLabel === g ? 700 : 400 }}>{g.slice(2)}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' }}>
              <Btn onClick={simulateGesture} color="var(--gold)">SIMULATE GESTURE →</Btn>
              {gestureLabel && (
                <>
                  <span style={{ fontSize: '20px' }}>{gestureLabel.split(' ')[0]}</span>
                  <Tag color="var(--gold)">{gestureLabel}</Tag>
                  <Tag color="var(--green)">{(gestureConf * 100).toFixed(1)}% confidence</Tag>
                </>
              )}
            </div>

            {gestureHistory.length > 1 && (
              <>
                <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '8px', letterSpacing: '.1em' }}>GESTURE HISTORY (last {gestureHistory.length})</div>
                <ResponsiveContainer width="100%" height={130}>
                  <BarChart data={gestureHistory}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                    <XAxis dataKey="gesture" stroke="var(--t3)" tick={{ fontSize: 8 }} angle={-15} textAnchor="end" height={40} interval={0} />
                    <YAxis stroke="var(--t3)" tick={{ fontSize: 8 }} domain={[0, 1]} tickFormatter={v => `${(v*100).toFixed(0)}%`} />
                    <Tooltip formatter={v => [`${(v*100).toFixed(1)}%`, 'confidence']} />
                    <Bar dataKey="score" radius={[2,2,0,0]}>
                      {gestureHistory.map((_, i) => <Cell key={i} fill={P[i % P.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </>
            )}
          </Panel>

          <Panel title="WEBCAM INTEGRATION CODE" accent="var(--gold)">
            <pre style={{ margin: 0, padding: '14px', background: 'var(--deep)', borderRadius: '3px', fontSize: '10px', color: 'var(--t2)', fontFamily: 'JetBrains Mono', lineHeight: 1.7, overflowX: 'auto' }}>
{`import { GestureRecognizer, FilesetResolver }
  from 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/vision_bundle.js';

const vision = await FilesetResolver.forVisionTasks('...wasm');
const recognizer = await GestureRecognizer.createFromOptions(vision, {
  baseOptions: { modelAssetPath: '...gesture_recognizer.task', delegate: 'GPU' },
  runningMode: 'VIDEO', numHands: 2,
});

// Real-time webcam loop
const video = document.querySelector('video');
let lastVideoTime = -1;
function detect() {
  if (video.currentTime !== lastVideoTime) {
    lastVideoTime = video.currentTime;
    const result = recognizer.recognizeForVideo(video, performance.now());
    // result.gestures[0][0].categoryName → 'Thumb_Up' etc
    // result.landmarks[0] → 21 hand landmarks
  }
  requestAnimationFrame(detect);
}
detect();`}
            </pre>
          </Panel>
        </div>
      )}

      {/* ── OBJECT ── */}
      {tab === 'object' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="OBJECT DETECTOR — EFFICIENTDET LITE" accent="var(--orange)">
            <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.7, marginBottom: '14px' }}>
              <strong style={{ color: 'var(--orange)' }}>EfficientDet-Lite0</strong> detects 80+ object categories with bounding boxes. Lite variant optimized for mobile/browser — ~3MB model. Outputs class name, confidence score, and pixel-space bounding box.
            </div>
            {objStatus !== 'ready' && (
              <ModelLoader status={objStatus} progress={objProgress} modelName="efficientdet_lite0" modelSize="~3MB" accent="var(--orange)" onLoad={loadObj} />
            )}
          </Panel>

          {objStatus === 'ready' && (
            <Panel title="DETECT OBJECTS" accent="var(--orange)">
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                <ImageUploader id="obj-img" src={objImgSrc} onSrc={src => { setObjImgSrc(src); setObjResult(null); }} color="var(--orange)" />
                {objImgSrc && <Btn onClick={runObj} color="var(--orange)">DETECT →</Btn>}
                {objTime && <InferenceTag ms={objTime} />}
              </div>
              {objImgSrc && (
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '14px', alignItems: 'start' }}>
                  <div>
                    <img id="obj-img" src={objImgSrc} alt="" style={{ display: 'none' }} crossOrigin="anonymous" />
                    <canvas ref={objCanvas} style={{ maxWidth: '360px', borderRadius: '4px', border: '1px solid var(--b0)', display: 'block' }} />
                  </div>
                  {objResult?.detections?.length > 0 && (
                    <div>
                      <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '8px', letterSpacing: '.1em' }}>{objResult.detections.length} OBJECTS</div>
                      {objResult.detections.map(({ categories, boundingBox }, i) => (
                        <div key={i} style={{ display: 'flex', gap: '10px', padding: '8px 10px', background: 'var(--raised)', borderRadius: '3px', marginBottom: '5px', border: `1px solid ${P[i % P.length]}33` }}>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: P[i % P.length], flex: 1 }}>{categories[0]?.categoryName}</span>
                          <span style={{ fontSize: '10px', color: 'var(--t3)', fontFamily: 'JetBrains Mono' }}>{(categories[0]?.score * 100).toFixed(0)}%</span>
                          <span style={{ fontSize: '9px', color: 'var(--t3)', fontFamily: 'JetBrains Mono' }}>
                            {Math.round(boundingBox?.width)}×{Math.round(boundingBox?.height)}px
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Panel>
          )}
        </div>
      )}

      {/* ── SEGMENT ── */}
      {tab === 'segment' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="IMAGE SEGMENTATION" accent="var(--red)">
            <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.75, marginBottom: '14px' }}>
              MediaPipe Image Segmenter produces pixel-level masks for 20 semantic categories (Pascal VOC). The selfie segmentation model isolates the human subject from background in real-time — used in Google Meet's background blur.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[
                { name:'Selfie Segmentation', size:'~0.5MB', desc:'Binary mask separating person from background. GPU-accelerated. Used for background blur, replacement, effects. Works at 30+fps on webcam.', color:'var(--red)' },
                { name:'General Segmenter', size:'~10MB', desc:'20-class semantic segmentation (person, car, bicycle, cat, dog, sky…). DeepLab architecture. Outputs confidence mask per class.', color:'var(--orange)' },
                { name:'Interactive Segmenter', size:'~8MB', desc:'Click-point prompted segmentation — click anywhere to isolate that object. Based on SAM (Segment Anything Model) concept adapted for real-time use.', color:'var(--violet)' },
                { name:'Hair Segmenter', size:'~1MB', desc:'High-accuracy hair region segmentation. Used for hair recoloring, virtual try-on. Distinct from general selfie mask — precise hair boundary detection.', color:'var(--cyan)' },
              ].map(({ name, size, desc, color }) => (
                <div key={name} style={{ padding: '14px', background: 'var(--raised)', border: `1px solid ${color}33`, borderRadius: '4px' }}>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color }}>{name}</span>
                    <Tag color={color}>{size}</Tag>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.65 }}>{desc}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── CONCEPTS ── */}
      {tab === 'concepts' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {[
            { title:'BlazePalm Detection Pipeline', color:ACCENT, desc:'Two-stage: detector finds bounding box → landmark model predicts precise 21 keypoints within that box. Same pattern for face (BlazeFace → FaceMesh) and pose (BlazePose → PoseLandmark). Separate detector runs infrequently; tracker is frame-to-frame, much faster.' },
            { title:'WASM + SIMD Acceleration', color:'var(--green)', desc:'MediaPipe compiles custom XNNPACK kernels to WebAssembly with SIMD (Single Instruction Multiple Data) — 4× scalar throughput for float32 ops. Combined with WebGL textures for matrix multiply, achieves 30fps inference on mobile. WASM SIMD requires Chrome 91+, Firefox 89+.' },
            { title:'3D Landmark Coordinates', color:'var(--violet)', desc:'MediaPipe outputs x, y (normalized 0→1 relative to image) and z (relative depth). Z is relative to the wrist for hands, hip midpoint for pose. Not absolute 3D — relative scale. Face landmarks include z from a 3D face model projection, enabling 3D face rotation matrix.' },
            { title:'GPU Delegate (WebGL)', color:'var(--gold)', desc:'When delegate: "GPU" is set, MediaPipe uses WebGL compute shaders for convolution layers. Falls back to WASM automatically if WebGL unavailable. GPU is 3-5× faster than WASM for large models. The delegate selection happens at model init time.' },
            { title:'Video Mode vs Image Mode', color:'var(--orange)', desc:'runningMode: "IMAGE" → stateless inference, each call independent. "VIDEO" → temporal tracking enabled: detector runs every N frames, tracker interpolates in between. Video mode is faster for continuous streams and produces smoother landmark trajectories (low-pass filtered).' },
            { title:'Model Architecture', color:'var(--red)', desc:'BlazeFace/BlazePalm: tiny anchor-free single-shot detector (<1MB, <1ms). FaceMesh: heatmap regression head on top of MobileNetV2 backbone. PoseLandmark: hourglass architecture for 33 keypoints + visibility. All converted to TFLite format, then compiled to WASM/WebGL by the Tasks runtime.' },
          ].map(({ title, color, desc }) => (
            <div key={title} style={{ padding: '16px', background: 'var(--raised)', border: `1px solid ${color}33`, borderRadius: '4px' }}>
              <div style={{ color, fontWeight: 700, fontSize: '13px', marginBottom: '8px' }}>{title}</div>
              <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.75 }}>{desc}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
